<template>
  <!-- ── Incoming Call Overlay ────────────────────────────────────────────── -->
  <Teleport to="body">
    <Transition name="call-overlay">
      <div
        v-if="intercomStore.hasIncomingCall && activeCall"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm"
        @keydown.esc="rejectCall">
        <div
          class="relative w-full max-w-md mx-4 bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <div
            class="relative h-40 bg-gradient-to-b from-blue-900/60 to-zinc-900 flex flex-col items-center justify-center gap-2">
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span
                v-for="i in 3"
                :key="i"
                class="absolute rounded-full border border-blue-400/30 animate-ping"
                :style="{
                  width: `${60 + i * 30}px`,
                  height: `${60 + i * 30}px`,
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: '1.5s',
                }" />
            </div>
            <div
              class="relative z-10 w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/40">
              <span class="material-symbols-outlined text-3xl text-white">door_front</span>
            </div>
            <span class="relative z-10 text-xs text-blue-300 font-medium tracking-widest uppercase">{{
              tt('Incoming Call', 'Dohodni klic')
            }}</span>
          </div>
          <div class="p-6">
            <h2 class="text-xl font-semibold text-center mb-1">{{ activeCall.cameraName }}</h2>
            <p class="text-sm text-zinc-400 text-center mb-6">
              {{ activeCall.fromUri || tt('SIP Doorphone', 'SIP domofon') }}
            </p>
            <div class="flex gap-3">
              <button
                @click="rejectCall"
                :disabled="intercomStore.isAnsweringCall"
                class="flex-1 flex items-center justify-center gap-2 py-3.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-xl text-white font-medium transition-colors">
                <span class="material-symbols-outlined text-xl">call_end</span>{{ tt('Reject', 'Zavrni') }}
              </button>
              <button
                @click="answerCall"
                :disabled="intercomStore.isAnsweringCall"
                class="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-xl text-white font-medium transition-colors">
                <span
                  v-if="intercomStore.isAnsweringCall"
                  class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <span v-else class="material-symbols-outlined text-xl">call</span>{{ tt('Answer', 'Sprejmi') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- ── Outbound Call Ringing ───────────────────────────────────────────── -->
  <Teleport to="body">
    <Transition name="call-overlay">
      <div
        v-if="intercomStore.isCalling && activeCall"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div
          class="relative w-full max-w-sm mx-4 bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <div class="p-6 flex flex-col items-center gap-3">
            <div
              class="relative w-16 h-16 rounded-full bg-green-600 flex items-center justify-center shadow-lg shadow-green-600/40">
              <span
                class="absolute inset-0 rounded-full border border-green-400/30 animate-ping"
                style="animationduration: '1.5s'"></span>
              <span class="material-symbols-outlined text-3xl text-white">door_front</span>
            </div>
            <h2 class="text-lg font-semibold text-center">{{ activeCall.cameraName }}</h2>
            <span class="text-xs text-zinc-400 font-medium tracking-widest uppercase">{{
              tt('Calling…', 'Klicanje…')
            }}</span>
            <button
              @click="hangupCall"
              class="flex items-center justify-center gap-2 w-full py-3 mt-2 bg-red-600 hover:bg-red-500 rounded-xl text-white font-medium transition-colors">
              <span class="material-symbols-outlined text-xl">call_end</span>
              {{ tt('Cancel', 'Prekliči') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- ── Active Call (camera-modal style frame) ───────────────────────────── -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="intercomStore.isInCall && activeCall"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
        <div
          class="relative w-full max-w-[90vw] h-[85vh] flex flex-col overflow-hidden rounded-2xl bg-black border border-gray-800 shadow-2xl">
          <!-- Video fills the frame -->
          <IntercomPlayer
            ref="playerRef"
            :cameraId="activeCall.cameraId"
            :whepUrl="intercomStore.streamEndpoint?.whepUrl"
            :iceServers="intercomStore.streamEndpoint?.iceServers"
            :video-muted="false"
            class="absolute inset-0 w-full h-full" />

          <!-- Top gradient header -->
          <div
            class="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-3 py-2 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
            <div class="flex items-center gap-2 min-w-0">
              <div
                class="w-9 h-9 rounded-full bg-blue-600/80 border border-blue-400/40 flex items-center justify-center shrink-0">
                <span class="material-symbols-outlined text-lg text-white">door_front</span>
              </div>
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-bold text-white drop-shadow-md truncate">{{ activeCall.cameraName }}</span>
                <span class="text-[10px] text-white/60 truncate">{{
                  activeCall.fromUri || tt('SIP Doorphone', 'SIP domofon')
                }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <div
                class="flex items-center gap-1.5 px-2.5 h-9 rounded-full bg-black/50 backdrop-blur-md border border-white/10">
                <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                <span class="text-[10px] font-bold tracking-widest text-white/90">{{ tt('LIVE', 'V ŽIVO') }}</span>
              </div>
              <div
                class="flex items-center justify-center h-9 px-3 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-sm font-mono text-white/90">
                {{ callDuration }}
              </div>
            </div>
          </div>

          <!-- Bottom control dock -->
          <div
            class="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center gap-2.5 px-4 pt-14 bg-gradient-to-t from-black/85 to-transparent"
            style="padding-bottom: calc(1rem + var(--safe-bottom))">
            <!-- Unlock -->
            <button
              @click="openDoor"
              :disabled="intercomStore.doorOpening || activeCall.doorOpened"
              :title="
                activeCall.doorOpened
                  ? tt('Door opened', 'Vrata odprta')
                  : intercomStore.doorOpening
                    ? tt('Opening…', 'Odpiranje…')
                    : tt('Unlock door', 'Odkleni vrata')
              "
              class="h-11 w-11 rounded-full border backdrop-blur-md flex items-center justify-center transition-colors active:scale-95 disabled:cursor-not-allowed"
              :class="
                activeCall.doorOpened
                  ? 'bg-green-600/80 border-green-400/40 text-white'
                  : intercomStore.doorOpening
                    ? 'bg-yellow-600/40 border-yellow-400/40 text-yellow-200 animate-pulse'
                    : 'bg-black/50 hover:bg-white/20 border-white/10 text-white'
              ">
              <span class="material-symbols-outlined text-xl">{{ activeCall.doorOpened ? 'lock_open' : 'lock' }}</span>
            </button>

            <!-- Mic mute -->
            <button
              @click="toggleMic"
              :title="micMuted ? tt('Unmute microphone', 'Vklopi mikrofon') : tt('Mute microphone', 'Izklopi mikrofon')"
              class="h-11 w-11 rounded-full border backdrop-blur-md flex items-center justify-center transition-colors active:scale-95"
              :class="
                micMuted
                  ? 'bg-red-600/80 hover:bg-red-600 border-red-400/40 text-white'
                  : 'bg-black/50 hover:bg-white/20 border-white/10 text-white'
              ">
              <span class="material-symbols-outlined text-xl">{{ micMuted ? 'mic_off' : 'mic' }}</span>
            </button>

            <!-- Speaker volume + mute -->
            <div
              class="flex items-center gap-2 h-11 pl-1.5 pr-3 rounded-full bg-black/50 backdrop-blur-md border border-white/10">
              <button
                @click="toggleSpeakerMute"
                :title="speakerMuted ? tt('Unmute speaker', 'Vklopi zvočnik') : tt('Mute speaker', 'Izklopi zvočnik')"
                class="h-8 w-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
                :class="speakerMuted || speakerVolume === 0 ? 'text-red-400' : 'text-white'">
                <span class="material-symbols-outlined text-xl">{{ speakerIcon }}</span>
              </button>
              <input
                v-model.number="speakerVolume"
                type="range"
                min="0"
                max="100"
                step="1"
                :title="tt('Speaker volume', 'Glasnost zvočnika')"
                class="w-24 md:w-32 cursor-pointer accent-blue-500" />
              <span
                class="w-9 text-right text-[10px] font-mono"
                :class="speakerMuted ? 'text-red-300' : 'text-white/70'">
                {{ speakerMuted ? tt('MUTE', 'TIŠINA') : speakerVolume + '%' }}
              </span>
            </div>

            <!-- End call -->
            <button
              @click="hangupCall"
              :title="tt('End call', 'Končaj klic')"
              class="h-11 w-11 rounded-full bg-red-600 hover:bg-red-500 border border-red-400/40 text-white flex items-center justify-center transition-colors active:scale-95">
              <span class="material-symbols-outlined text-xl">call_end</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useIntercomStore } from '../stores/intercom';
import { useSocketStore } from '../stores/socket';
import { useCameraModal } from '../composables/useCameraModal';
import IntercomPlayer from './IntercomPlayer.vue';
import { tt } from '../utils/consoleText';

const intercomStore = useIntercomStore();
const socketStore = useSocketStore();
const cameraModal = useCameraModal();
const route = useRoute();
const isCloudLandingPage = computed(
  () =>
    route.path === '/cloud' ||
    route.path === '/home' ||
    route.path === '/servers' ||
    (!!route.meta.cloudView && !route.params.hubId),
);

const playerRef = ref<InstanceType<typeof IntercomPlayer> | null>(null);
const callDuration = ref('0:00');
const micMuted = ref(false);
let durationTimer: ReturnType<typeof setInterval> | null = null;

// ─── Speaker volume / mute (remote audio) ────────────────────────────────────

const SPEAKER_VOLUME_KEY = 'otto.intercom.speakerVolume';

function loadSpeakerVolume(): number {
  const stored = Number(localStorage.getItem(SPEAKER_VOLUME_KEY));
  return Number.isFinite(stored) && stored >= 0 && stored <= 100 ? stored : 100;
}

const speakerVolume = ref<number>(loadSpeakerVolume());
const speakerMuted = ref(false);

const effectiveSpeakerVolume = computed(() =>
  speakerMuted.value ? 0 : Math.max(0, Math.min(1, speakerVolume.value / 100)),
);

const speakerIcon = computed(() => {
  if (speakerMuted.value || speakerVolume.value === 0) return 'volume_off';
  return speakerVolume.value <= 40 ? 'volume_down' : 'volume_up';
});

function toggleSpeakerMute(): void {
  speakerMuted.value = !speakerMuted.value;
}

/** Push the current volume to the SIP playback gain and the video element. */
function applySpeakerVolume(): void {
  if (masterGain) {
    masterGain.gain.value = effectiveSpeakerVolume.value;
  }
  playerRef.value?.setAudioVolume(effectiveSpeakerVolume.value);
}

const activeCall = computed(() => intercomStore.activeCall);

// ─── G.711 µ-law / A-law codecs ───────────────────────────────────────────────

function buildUlawTable(): Float32Array {
  // Canonical µ-law expansion (G.711): the bias (0x84) is INSIDE the
  // shifted value — subtracting it before the shift attenuates and
  // distorts everything.
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const u = ~i & 0xff;
    let sample = ((u & 0x0f) << 3) + 0x84;
    sample <<= (u & 0x70) >> 4;
    sample = u & 0x80 ? 0x84 - sample : sample - 0x84;
    t[i] = sample / 32768;
  }
  return t;
}

function buildAlawTable(): Float32Array {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const a = i ^ 0x55;
    const mant = (a & 0x0f) << 4;
    const seg = (a & 0x70) >> 4;
    let sample: number;
    if (seg === 0) sample = mant + 8;
    else if (seg === 1) sample = mant + 0x108;
    else {
      sample = (mant + 0x108) << (seg - 1);
    }
    sample = a & 0x80 ? sample : -sample;
    t[i] = sample / 32768;
  }
  return t;
}

const ULAW_TABLE = buildUlawTable();
const ALAW_TABLE = buildAlawTable();

function linear2ulaw(sample: number): number {
  // Canonical µ-law companding (G.711), matching the sun/ITU reference
  // encoder: exponent = floor(log2(s >> 7)) + 1, clamped to 7.
  const BIAS = 0x84;
  const CLIP = 32635;
  const clamped = Math.max(-1, Math.min(1, sample));
  let s = Math.round(clamped * 32767); // 32767, not 32768 — avoid sign-bit overflow
  const sign = (s >> 8) & 0x80;
  if (sign !== 0) s = -s;
  if (s > CLIP) s = CLIP;
  s += BIAS;
  const exponent = Math.min(7, Math.floor(Math.log2(s >> 7)) + 1);
  const mantissa = (s >> (exponent + 3)) & 0x0f;
  return ~(sign | (exponent << 4) | mantissa) & 0xff;
}

// ─── SIP RTP audio pipeline (doorphone → UI) ────────────────────────────────

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let nextPlayAt = 0;
const sipAudioActive = ref(false);
let socketBound = false;

function ensureAudioContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtx = new Ctx();
      // Master speaker gain — sits between all remote-audio sources and the
      // output so the volume slider / mute toggle apply to the whole call.
      masterGain = audioCtx.createGain();
      masterGain.gain.value = effectiveSpeakerVolume.value;
      masterGain.connect(audioCtx.destination);
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => undefined);
    }
    return audioCtx;
  } catch {
    return null;
  }
}

function playSipAudio(base64: string, payloadType: number): void {
  const ctx = ensureAudioContext();
  if (!ctx) return;
  let bytes: Uint8Array;
  try {
    bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  } catch {
    return;
  }
  if (bytes.length === 0) return;
  const table = payloadType === 8 ? ALAW_TABLE : ULAW_TABLE;
  const src = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) src[i] = table[bytes[i]];
  // Resample 8 kHz → context rate (linear interpolation)
  const ratio = ctx.sampleRate / 8000;
  const outLen = Math.max(1, Math.round(src.length * ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = (i * 8000) / ctx.sampleRate;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, src.length - 1);
    out[i] = src[i0] + (src[i1] - src[i0]) * (pos - i0);
  }
  const buf = ctx.createBuffer(1, outLen, ctx.sampleRate);
  buf.getChannelData(0).set(out);
  const node = ctx.createBufferSource();
  node.buffer = buf;
  node.connect(masterGain ?? ctx.destination);
  const now = ctx.currentTime;
  nextPlayAt = Math.max(now + 0.02, nextPlayAt);
  node.start(nextPlayAt);
  nextPlayAt += outLen / ctx.sampleRate;

  // First SIP audio packet — mute the go2rtc audio path to avoid double audio
  if (!sipAudioActive.value) {
    sipAudioActive.value = true;
    playerRef.value?.setAudioMuted(true);
  }
}

function onSipAudio(data: { callId?: string; payload?: unknown; payloadType?: number }): void {
  if (!intercomStore.isInCall) return;
  if (!data?.callId || activeCall.value?.id !== data.callId) return;
  if (!data.payload) return;
  playSipAudio(data.payload as string, data.payloadType || 0);
}

// ─── Mic (UI → doorphone over SIP RTP) ───────────────────────────────────────

let micStream: MediaStream | null = null;
let micSource: MediaStreamAudioSourceNode | null = null;
let micProcessor: ScriptProcessorNode | null = null;
let micAccum: number[] = []; // pending 8 kHz samples
let micInputRate = 48000;

async function startMic(): Promise<void> {
  if (micStream || !activeCall.value) return;
  const ctx = ensureAudioContext();
  if (!ctx) return;
  try {
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    micMuted.value = true;
    return;
  }
  const track = micStream.getAudioTracks()[0];
  if (track) micInputRate = track.getSettings().sampleRate || ctx.sampleRate;
  micSource = ctx.createMediaStreamSource(micStream);
  micProcessor = ctx.createScriptProcessor(4096, 1, 1);
  micProcessor.onaudioprocess = (e: AudioProcessingEvent) => {
    if (!intercomStore.isInCall || micMuted.value) return;
    const input = e.inputBuffer.getChannelData(0);
    const downsample = Math.round(micInputRate / 8000);
    for (let i = 0; i < input.length; i += downsample) {
      micAccum.push(Math.max(-1, Math.min(1, input[i])));
    }
    while (micAccum.length >= 160) {
      const chunk = micAccum.splice(0, 160);
      const bytes = new Uint8Array(160);
      for (let i = 0; i < 160; i++) bytes[i] = linear2ulaw(chunk[i]);
      socketStore.socket?.emit('intercom:audio_send', {
        callId: activeCall.value?.id,
        payload: btoa(String.fromCharCode(...bytes)),
      });
    }
  };
  micSource.connect(micProcessor);
  micProcessor.connect(ctx.destination);
  micMuted.value = false;
}

function stopMic(): void {
  try {
    micProcessor?.disconnect();
    micSource?.disconnect();
    micStream?.getTracks().forEach((t) => t.stop());
  } catch {
    // ignore
  }
  micProcessor = null;
  micSource = null;
  micStream = null;
  micAccum = [];
}

function stopAudioPipeline(): void {
  stopMic();
  if (audioCtx) {
    audioCtx.close().catch(() => undefined);
    audioCtx = null;
  }
  masterGain = null;
  nextPlayAt = 0;
  sipAudioActive.value = false;
}

// Close the camera live-view modal when a call takes over the screen —
// its z-index (9999) would otherwise cover the call controls
watch(
  () => intercomStore.hasIncomingCall || intercomStore.isCalling || intercomStore.isInCall,
  (active) => {
    if (active) {
      cameraModal.close();
    }
  },
);

// Start/stop the SIP audio pipeline with the active call
watch(
  () => intercomStore.isInCall,
  (active) => {
    if (active) {
      ensureAudioContext();
      startMic().catch(() => {
        // mic requires a user gesture — user can tap the mic button
      });
      // Player mounts with the call — apply the stored volume once rendered
      nextTick(() => applySpeakerVolume());
    } else {
      stopAudioPipeline();
    }
  },
);

// Keep the speaker volume in sync with the SIP gain + video element
watch([speakerVolume, speakerMuted], () => {
  localStorage.setItem(SPEAKER_VOLUME_KEY, String(speakerVolume.value));
  applySpeakerVolume();
});

// Attach the socket listener when the socket becomes available
watch(
  () => socketStore.socket,
  (socket) => {
    if (socket && !socketBound) {
      socketBound = true;
      socket.on('intercom:audio', onSipAudio);
    }
  },
  { immediate: true },
);

async function answerCall() {
  if (!activeCall.value) return;
  await intercomStore.answerCall(activeCall.value.id);
  startDurationTimer();
}

async function rejectCall() {
  if (!activeCall.value) return;
  await intercomStore.rejectCall(activeCall.value.id);
}

async function hangupCall() {
  if (!activeCall.value) return;
  await intercomStore.hangupCall(activeCall.value.id);
  stopDurationTimer();
}

async function openDoor() {
  if (!activeCall.value) return;
  await intercomStore.openDoor(activeCall.value.id);
}

async function toggleMic() {
  if (micStream) {
    stopMic();
    micMuted.value = true;
  } else {
    await startMic();
  }
}

function startDurationTimer() {
  stopDurationTimer();
  const start = Date.now();
  durationTimer = setInterval(() => {
    const elapsed = Math.floor((Date.now() - start) / 1000);
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    callDuration.value = `${m}:${String(s).padStart(2, '0')}`;
  }, 1000);
}

function stopDurationTimer() {
  if (durationTimer) {
    clearInterval(durationTimer);
    durationTimer = null;
  }
  callDuration.value = '0:00';
}

onMounted(() => {
  if (isCloudLandingPage.value) {
    return;
  }
  intercomStore.initialize();
});

onBeforeUnmount(() => {
  socketStore.socket?.off('intercom:audio', onSipAudio);
  socketBound = false;
});

onUnmounted(() => {
  stopDurationTimer();
  stopAudioPipeline();
});
</script>

<style scoped>
.call-overlay-enter-active,
.call-overlay-leave-active {
  transition: opacity 0.25s ease;
}
.call-overlay-enter-from,
.call-overlay-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
